// src/lib/posthog/init-options.ts
//
// Every posthog-js capture option, fixed in code so no capture behaviour is
// left to the project's remote configuration. `before_send` is a parameter,
// not an import: `client.ts` passes in the redaction hook from `sanitize.ts`.

import type {
  BeforeSendFn,
  PostHogConfig,
  RageclickConfig,
  SessionRecordingOptions,
} from "posthog-js";

import { firstPartyNetworkRequest } from "./replay-policy";

// An object, never `rageclick: true`; ≤10 entries in content_ignorelist.
const RAGECLICK_CONFIG: RageclickConfig = {
  content_ignorelist: [
    "next",
    "previous",
    "prev",
    ">",
    "<",
    "+",
    "-",
    "−",
    "–",
  ],
  ignore_text_selection: true,
};

// `[data-ph-mask]` plus the sonner and react-hot-toast toast containers.
const MASK_TEXT_SELECTOR = "[data-ph-mask], [data-sonner-toaster], [data-rht-toaster]";

// `[data-ph-no-capture]` plus the Next dev overlay's custom element.
const BLOCK_SELECTOR = "[data-ph-no-capture], nextjs-portal";

// Network capture (rrweb/network@1) records every request the page makes,
// including third-party trackers' own requests, which carry the page URL and
// referrer in their request URL, so only first-party requests are kept. The
// function reads `window` only when the recorder calls it, in the browser.
// Setting it replaces posthog-js's default network cleaning, which also
// scrubbed request and response bodies, so bodies and headers are switched
// off here explicitly rather than left to the project's remote settings.
const SESSION_RECORDING_OPTIONS: SessionRecordingOptions = {
  maskAllInputs: true,
  maskInputOptions: { password: true, email: true },
  maskTextSelector: MASK_TEXT_SELECTOR,
  blockSelector: BLOCK_SELECTOR,
  recordBody: false,
  recordHeaders: false,
  maskCapturedNetworkRequestFn: (request) =>
    firstPartyNetworkRequest(request, window.location.origin),
};

export interface BuildInitOptionsParams {
  /** Resolved `NEXT_PUBLIC_POSTHOG_HOST` (`config.ts`'s `resolveIngestHost`). */
  apiHost: string;
  /** Fixed EU UI/API host (`config.ts`'s `getPosthogClientEnv().uiHost`). */
  uiHost: string;
  /** The URL-redaction hook, implemented by `sanitize.ts`. */
  beforeSend: BeforeSendFn;
}

/**
 * Every posthog-js capture option fixed in code. Deliberately never sets
 * `opt_out_capturing_by_default`: the pending state is decided by `consent.ts`
 * alone. Recording starts disabled; `applyReplayPolicy` (client.ts) calls
 * `startSessionRecording()` only on allowed surfaces.
 */
export function buildInitOptions(
  params: BuildInitOptionsParams,
): Partial<PostHogConfig> {
  return {
    api_host: params.apiHost,
    ui_host: params.uiHost,
    person_profiles: "always",
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    capture_heatmaps: true,
    capture_dead_clicks: true,
    capture_exceptions: false,
    capture_performance: true,
    persistence: "localStorage+cookie",
    // The `/flags/` request carries `person_properties.$initial_current_url`
    // (the raw page URL) past `before_send`, which never sees it. The app
    // reads no feature flag, so the request is switched off entirely — the
    // option is `advanced_disable_flags`, not `advanced_disable_feature_flags`
    // (that one keeps the request and only stops evaluation). This also turns
    // off remote config, which is fine: every capture option here is fixed.
    advanced_disable_flags: true,
    disable_session_recording: true,
    enable_recording_console_log: false,
    session_recording: SESSION_RECORDING_OPTIONS,
    rageclick: RAGECLICK_CONFIG,
    before_send: params.beforeSend,
  };
}
